import React, { useMemo } from 'react';
import { Handle, Position, Connection } from '@xyflow/react';
import { useNodeStore } from '@/stores/node-store';
import {
  getCategoryConfig,
  GREY_COLOR,
  ERROR_RED,
  HANDLE_STYLING,
  HandleTypes,
} from '@/constants/node-styling';
import { getHandleClassName } from '@/pages/workflow/helper/get-handle-class-name';
import PortTooltip from './port-tooltip';
import { ConnectionType, CustomConnection } from '@/types/edge-types';
import { isArtifactEdge } from '@/pages/workflow/helper/artifact-node-manager';

interface PortInfo {
  id: string;
  display_name: string;
  info?: string;
  port_type: { accepted_types: string[] };
}

interface NodeHandleProps {
  port: PortInfo;
  nodeId: string;
  direction: 'input' | 'output';
  isSingleHandle?: boolean;
  config: ReturnType<typeof getCategoryConfig>;

  // Optional callbacks
  edgeType?: ConnectionType;
  onConnectCallback?: (connection: CustomConnection) => void;

  // Styling options
  isLocked?: boolean; // If true, handle displays grey color
  hasValidationError?: boolean; // If true, handle displays red color for validation error
}

/**
 * Reusable NodeHandle component that wraps React Flow's Handle with
 * PortTooltip and consistent styling across the application.
 */
export const NodeHandle: React.FC<NodeHandleProps> = ({
  port,
  nodeId,
  direction,
  isSingleHandle = false,
  config,
  edgeType,
  onConnectCallback,
  isLocked = false,
  hasValidationError = false,
}) => {
  const {
    nodes,
    edges,
    validateConnection,
    activeConnect,
    isValidEdgeConnection,
    onConnect,
  } = useNodeStore();

  const isOutput = direction === 'output';
  const handleType = isOutput ? 'source' : 'target';
  const position = isOutput ? Position.Right : Position.Left;
  const tooltipSide = isOutput ? 'right' : 'left';

  // Build hover animation classes
  // Both direct hover and group-hover for consistent behavior
  const hoverAnimationClasses = isSingleHandle
    ? isOutput
      ? 'hover:!w-[10px] hover:!right-[-10px] group-hover:!w-[10px] group-hover:!right-[-10px]'
      : 'hover:!w-[10px] hover:!left-[-10px] group-hover:!w-[10px] group-hover:!left-[-10px]'
    : isOutput
      ? '!right-[-24px] hover:!w-[10px] hover:!right-[-28px] group-hover:!w-[10px] group-hover:!right-[-28px]'
      : '!left-[-24px] hover:!w-[10px] hover:!left-[-28px] group-hover:!w-[10px] group-hover:!left-[-28px]';

  // Handle connection callback
  const handleConnect = (connection: Connection) => {
    if (onConnectCallback) {
      const connectionWithType: CustomConnection = {
        ...connection,
        edge_type: edgeType || ConnectionType.PORT_TO_PORT,
      };
      onConnectCallback(connectionWithType);
    } else if (edgeType) {
      // Default behavior: use store's onConnect with edge type
      const connectionWithType: CustomConnection = {
        ...connection,
        edge_type: edgeType,
      };
      onConnect(connectionWithType);
    }
  };

  // Validate connection
  const handleIsValidConnection = (edge: {
    source: string;
    target: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
  }) => {
    const connection: Connection = {
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle ?? null,
      targetHandle: edge.targetHandle ?? null,
    };
    return isValidEdgeConnection(connection);
  };

  const artifactHandleStyle = useMemo((): React.CSSProperties | null => {
    if (direction !== 'output') return null;
    const portEdges = edges.filter(
      (e) => e.source === nodeId && e.sourceHandle === port.id,
    );
    if (portEdges.length === 0) return null;
    if (!portEdges.every((e) => isArtifactEdge(e))) return null;
    const fileColor = HANDLE_STYLING[HandleTypes.PARAM_FILE].color;
    return { backgroundColor: fileColor, color: fileColor };
  }, [direction, edges, nodeId, port.id]);

  // Priority: validation error (red) > locked (grey) > artifact-only (gradient) > category handle styling
  const handleStyle = useMemo((): React.CSSProperties => {
    if (hasValidationError) {
      return { backgroundColor: ERROR_RED, color: ERROR_RED };
    }
    if (isLocked) {
      return { backgroundColor: GREY_COLOR, color: GREY_COLOR };
    }
    if (artifactHandleStyle) {
      return artifactHandleStyle;
    }
    const hs = config.handleStyling;
    if ('background' in hs && hs.background) {
      return { background: hs.background, color: hs.color };
    }
    const fill =
      'backgroundColor' in hs && hs.backgroundColor != null
        ? hs.backgroundColor
        : hs.color;
    return { backgroundColor: fill, color: hs.color };
  }, [hasValidationError, isLocked, artifactHandleStyle, config]);

  return (
    <PortTooltip
      port={{
        display_name: port.display_name,
        info: port.info || '',
        accepted_types: port.port_type.accepted_types,
      }}
      side={tooltipSide}
    >
      <Handle
        id={port.id}
        type={handleType}
        position={position}
        isConnectable={!isLocked}
        style={handleStyle}
        className={`${config.handleClass} ${getHandleClassName({
          activeConnect,
          nodes,
          currentNodeId: nodeId,
          currentHandleId: port.id,
          handleType,
          validateConnection,
        })} transition-all duration-200 ${hoverAnimationClasses}`}
        onConnect={handleConnect}
        isValidConnection={handleIsValidConnection}
      />
    </PortTooltip>
  );
};
